import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { User, Menu, X, ChevronDown, Globe, DollarSign, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../store/userSlice';
import LoginModal from './LoginModal';
import LanguageSwitcher from './LanguageSwitcher';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // 使用国际化
  const { t, i18n } = useTranslation();
  
  // 使用Redux状态替代本地用户状态
  const dispatch = useDispatch();
  const { user, isLoggedIn } = useSelector((state) => state.user);
  
  const location = useLocation();

  // 移除复杂的事件监听，使用页面刷新来确保状态同步

  // 根据登录状态动态生成导航项
  const getNavItems = () => {
    const items = [
      { name: t('navigation.home'), href: '/', requireAuth: false },
      { name: 'ChatGPT Plus', href: '/chatgpt-plus', requireAuth: false },
      { name: 'Product Detail', href: '/product-detail', requireAuth: false },
      // { name: t('navigation.aiFleet'), href: '/ai-fleet', requireAuth: true }, // Temporarily disabled
      { name: t('navigation.myOrders'), href: '/orders', requireAuth: true },
      { name: t('navigation.selfServiceRenewal'), href: '/renewal', requireAuth: false },
      { name: t('navigation.emailInbox'), href: '/email-inbox', requireAuth: false },
      { name: t('navigation.support'), href: '/support', requireAuth: false }
    ];
    
    // 如果用户已登录，返回所有菜单项；否则只返回不需要认证的菜单项
    return items.filter(item => !item.requireAuth || isLoggedIn);
  };

  const navItems = getNavItems();

  // 处理登录成功 - 现在由Redux管理，不需要本地处理
  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
  };

  // 处理登出 - 使用Redux action
  const handleLogout = () => {
    dispatch(logoutUser());
    setIsUserMenuOpen(false);
  };

  // 检查当前路径是否匹配
  const isActiveRoute = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // 关闭移动端菜单
  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-purple-500 sticky top-0 z-50">
      {/* 单行Header - 合并语言货币选择和主导航 */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* 左侧 - Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link 
                to="/"
                className="flex items-center hover:opacity-80 transition-opacity duration-300"
              >
                <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-3 border border-white/30 overflow-hidden">
                  <img
                    src="/images/logo192.png"
                    alt="紫刀AI Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">{t('header.brandName')}</h1>
                  <p className="text-[11px] text-white/70 truncate max-w-[180px]">{t('header.brandSlogan')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* 中间 - 桌面端导航 */}
          <nav className="hidden md:block flex-1">
            <div className="flex items-center justify-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    isActiveRoute(item.href)
                      ? 'bg-white/20 backdrop-blur-sm text-white border border-white/30'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>

          {/* 右侧 - 语言货币 + 用户登录 + 移动端菜单 */}
          <div className="flex items-center space-x-4">
            
            {/* 语言选择器 - 使用LanguageSwitcher组件 */}
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            {/* 货币显示 - 固定美元 */}
            <div className="hidden md:block">
              <div className="flex items-center space-x-2 text-sm text-white/80">
                <DollarSign className="w-4 h-4" />
                <span>{t('header.currency')}</span>
              </div>
            </div>
            
            {/* 用户登录状态 */}
            {isLoggedIn && user ? (
              // 已登录状态
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                    <span className="text-white text-sm font-semibold">
                      {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium">{user.name || user.email || t('common.user')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
                {/* 用户菜单 */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <Link
                      to="/orders"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-300"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <span className="w-4 h-4 mr-3">📦</span>
                      {t('header.userMenu.myOrders')}
                    </Link>
                    <div className="border-t border-gray-200 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-300"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      {t('header.userMenu.logout')}
                    </button>
                </div>
              )}
            </div>
            ) : (
              // 未登录状态
              <button 
                onClick={() => setIsLoginModalOpen(true)}
                className="hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
            >
              <User className="h-5 w-5" />
              <span className="text-sm font-medium">{t('navigation.login')}</span>
              </button>
            )}

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/10 backdrop-blur-sm border-t border-white/20">
          <div className="px-2 pt-2 pb-3 space-y-1">
            
            {/* 移动端语言和货币显示 */}
            <div className="px-3 py-3 border-b border-white/20 mb-3">
              <div className="flex items-center justify-center space-x-6">
                  <LanguageSwitcher className="mobile" />
                <div className="flex items-center space-x-2 text-white/80">
                    <DollarSign className="w-4 h-4" />
                  <span className="text-sm">{t('header.currency')}</span>
                </div>
              </div>
            </div>
            
            {/* 移动端导航菜单 */}
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'bg-white/20 backdrop-blur-sm text-white border border-white/30'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* 移动端登录 */}
            <div className="border-t border-white/20 pt-3 mt-3">
              {isLoggedIn && user ? (
                <>
                  <div className="flex items-center px-3 py-2 text-white">
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mr-3 border border-white/30">
                      <span className="text-white text-sm font-semibold">
                        {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <span className="font-medium">{user.name || user.email || t('common.user')}</span>
                  </div>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center px-3 py-2 text-red-200 hover:text-white hover:bg-red-500/20 rounded-lg transition-all duration-300 w-full"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    {t('navigation.logout')}
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => {
                    setIsLoginModalOpen(true);
                    closeMobileMenu();
                  }}
                  className="flex items-center px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300 w-full"
              >
                <User className="h-5 w-5 mr-3" />
                {t('navigation.loginRegister')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 点击外部关闭下拉菜单 */}
      {isUserMenuOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsUserMenuOpen(false);
          }}
        />
      )}

      {/* 登录弹窗 */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLoginSuccess}
      />
    </header>
  );
}